import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { INTAKES_ENDPOINT } from '../../../config/api';

const defaultFormValues = {
    applicantName: '',
    applicantEmail: '',
    applicantPhone: '',
    message: '',
    intakeId: ''
};

const formatIntakeLabel = (intake) => {
    if (!intake) {
        return '';
    }
    const parts = [intake.name];
    if (intake.year) {
        parts.push(intake.year);
    }
    const start = intake.startDate ? new Date(intake.startDate).toLocaleDateString() : '';
    const end = intake.endDate ? new Date(intake.endDate).toLocaleDateString() : '';
    const range = start && end ? ` (${start} - ${end})` : start ? ` (starts ${start})` : '';
    return `${parts.filter(Boolean).join(' ')}${range}`.trim();
};

const ApplyModal = ({ show, course, onClose, onSubmit, status }) => {
    const [formValues, setFormValues] = useState(defaultFormValues);
    const [intakes, setIntakes] = useState([]);
    const [loadingIntakes, setLoadingIntakes] = useState(false);
    const [intakeError, setIntakeError] = useState('');
    const [validationError, setValidationError] = useState('');
    const { user } = useAuth();

    const autoFillValues = useMemo(() => {
        const userName = (() => {
            if (!user) {
                return '';
            }
            const { firstName, lastName, username, email } = user;
            const fullName = `${firstName || ''} ${lastName || ''}`.trim();
            if (fullName) {
                return fullName;
            }
            if (username) {
                return username;
            }
            return email || '';
        })();

        const userPhone = user?.phone || user?.phoneNumber || user?.contactNumber || '';

        return {
            applicantName: userName,
            applicantEmail: user?.email || '',
            applicantPhone: userPhone,
            message: course
                ? `I would like to apply for the ${course.programName} program at ${course.universityName}.`
                : '',
            intakeId: ''
        };
    }, [user, course]);

    useEffect(() => {
        if (show) {
            setFormValues(autoFillValues);
            setValidationError('');
        } else {
            setFormValues(defaultFormValues);
            setValidationError('');
        }
    }, [show, autoFillValues]);

    useEffect(() => {
        let cancelled = false;

        const fetchIntakes = async () => {
            setLoadingIntakes(true);
            setIntakeError('');
            try {
                const response = await fetch(INTAKES_ENDPOINT);
                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Unable to load intakes.');
                }
                const payload = await response.json();
                if (cancelled) {
                    return;
                }
                const items = Array.isArray(payload.intakes) ? payload.intakes : [];
                setIntakes(items);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                setIntakeError(error.message || 'Unable to load intakes.');
                setIntakes([]);
            } finally {
                if (!cancelled) {
                    setLoadingIntakes(false);
                }
            }
        };

        if (show) {
            fetchIntakes();
        } else {
            setIntakes([]);
            setLoadingIntakes(false);
            setIntakeError('');
        }

        return () => {
            cancelled = true;
        };
    }, [show]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        if (name === 'intakeId') {
            setValidationError('');
        }
        setFormValues((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (loadingIntakes) {
            return;
        }
        if (intakeError) {
            setValidationError('Unable to submit without a valid intake selection.');
            return;
        }
        if (!formValues.intakeId) {
            setValidationError('Please select an intake.');
            return;
        }
        if (typeof onSubmit === 'function') {
            onSubmit(formValues);
        }
    };

    const isSubmitting = status?.state === 'submitting';
    const isSuccess = status?.state === 'success';
    const isError = status?.state === 'error';

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Apply for this course</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {course && (
                    <div className="mb-3">
                        <strong>{course.programName}</strong>
                        <div>{course.universityName}</div>
                    </div>
                )}

                {isSuccess && status?.message && (
                    <Alert variant="success">
                        {status.message}
                    </Alert>
                )}

                {isError && status?.message && (
                    <Alert variant="danger">
                        {status.message}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="applicantName">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="applicantName"
                            value={formValues.applicantName}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            required
                            disabled={isSubmitting || isSuccess}
                        />
                    </Form.Group>

                    <Form.Group controlId="applicantEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="applicantEmail"
                            value={formValues.applicantEmail}
                            onChange={handleChange}
                            placeholder="name@example.com"
                            required
                            disabled={isSubmitting || isSuccess}
                        />
                    </Form.Group>

                    <Form.Group controlId="applicantPhone">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                            type="tel"
                            name="applicantPhone"
                            value={formValues.applicantPhone}
                            onChange={handleChange}
                            placeholder="Optional"
                            disabled={isSubmitting || isSuccess}
                        />
                    </Form.Group>

                    <Form.Group controlId="intakeId">
                        <Form.Label>Preferred Intake</Form.Label>
                        {loadingIntakes ? (
                            <div className="mb-2">
                                <Spinner animation="border" role="status" size="sm" />
                            </div>
                        ) : (
                            <Form.Control
                                as="select"
                                name="intakeId"
                                value={formValues.intakeId}
                                onChange={handleChange}
                                disabled={isSubmitting || isSuccess || !!intakeError || loadingIntakes}
                                required
                            >
                                <option value="">Select an intake</option>
                                {intakes.map((intake) => (
                                    <option key={intake.id || intake._id} value={intake.id || intake._id}>
                                        {formatIntakeLabel(intake)}
                                    </option>
                                ))}
                            </Form.Control>
                        )}
                        {intakeError && (
                            <Alert variant="danger" className="mt-2">
                                {intakeError}
                            </Alert>
                        )}
                        {!loadingIntakes && intakes.length === 0 && !intakeError && (
                            <Form.Text className="text-muted">
                                No intake windows are available right now.
                            </Form.Text>
                        )}
                        {validationError && !intakeError && (
                            <Form.Text className="text-danger">{validationError}</Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group controlId="message">
                        <Form.Label>Message</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="message"
                            value={formValues.message}
                            onChange={handleChange}
                            placeholder="Share any additional information"
                            disabled={isSubmitting || isSuccess}
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="secondary" onClick={onClose} className="mr-2">
                            Close
                        </Button>
                        <Button
                            type="submit"
                            variant="success"
                            disabled={
                                isSubmitting || isSuccess || loadingIntakes || !!intakeError
                            }
                        >
                            {isSubmitting ? 'Submitting...' : isSuccess ? 'Submitted' : 'Submit Application'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ApplyModal;
